import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "vg_cookie_consent";

function readConsent() {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
}

export default function CookieBanner() {
  // Lazy initializer: avoids the set-state-in-effect lint warning.
  const [shouldShow] = useState(() => readConsent() === null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!shouldShow) return undefined;
    const t = setTimeout(() => setVisible(true), 700);
    return () => clearTimeout(t);
  }, [shouldShow]);

  const persist = (value) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ value, ts: new Date().toISOString() })
      );
    } catch (e) {
      // ignore
    }
    setVisible(false);
  };

  if (!shouldShow || !visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Informativa sui cookie"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 md:bottom-6 z-[60] flex justify-center pointer-events-none"
    >
      <div className="pointer-events-auto w-full max-w-[920px] bg-white rounded-2xl border border-[#E5E5E5] shadow-[0_20px_50px_rgba(0,0,0,0.18)] overflow-hidden relative">
        <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-6 p-5 md:p-6">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-11 h-11 rounded-xl bg-[#B40101]/10 flex items-center justify-center flex-shrink-0">
              <Cookie className="w-5 h-5 text-[#B40101]" strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-[#0A0A0A] text-[17px] md:text-[18px] tracking-tight mb-1">
                Rispettiamo la tua privacy
              </h3>
              <p className="text-[#0A0A0A]/75 text-[14px] md:text-[15px] leading-relaxed">
                Usiamo solo cookie tecnici essenziali per far funzionare il sito.{" "}
                <strong>Nessun cookie di profilazione, nessuna pubblicità.</strong>{" "}
                Leggi la{" "}
                <a
                  data-testid="cookie-banner-policy-link"
                  href="/cookie"
                  className="text-[#B40101] font-semibold hover:underline whitespace-nowrap"
                >
                  Cookie Policy
                </a>
                .
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 md:gap-3 flex-shrink-0">
            <button
              data-testid="cookie-banner-reject"
              onClick={() => persist("rejected")}
              className="px-4 md:px-5 py-2.5 md:py-3 rounded-xl text-[14px] md:text-[15px] font-semibold text-[#0A0A0A] bg-[#F5F5F5] hover:bg-[#E9E9E9] transition-colors duration-200"
            >
              Rifiuta
            </button>
            <button
              data-testid="cookie-banner-accept"
              onClick={() => persist("accepted")}
              className="px-5 md:px-6 py-2.5 md:py-3 rounded-xl text-[14px] md:text-[15px] font-semibold text-white bg-[#B40101] hover:bg-[#8E0101] transition-all duration-200 hover:-translate-y-0.5 shadow-[0_8px_20px_rgba(180,1,1,0.25)]"
            >
              Accetta
            </button>
          </div>

          <button
            data-testid="cookie-banner-close"
            onClick={() => persist("dismissed")}
            aria-label="Chiudi"
            className="absolute top-3 right-3 md:hidden p-1.5 text-[#0A0A0A]/50 hover:text-[#0A0A0A]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
